import { Box, Divider, InputAdornment, SxProps, TextField, Typography } from "@mui/material";
import { useNutritionFlags } from "apps/web/src/hooks/useNutritionFlags";
import { useMyScoringSystemQuery } from "apps/web/src/types";
import { Dispatch, useState, type ReactNode } from "react";
import { Loading } from "shared-ui/global/Loading";
import { FoodFormUiProvider, type FoodFormUiVariant } from "../../FoodFormUi/FoodFormUiContext";
import NutrientAmountModal from "../../NutrientAmount/Modal";
import Details from "./Details";
import Images from "./Images/Images";
import { Ingredients } from "./Ingredients";
import { Instructions } from "./Instructions";
import RecipeFormServing from "./Serving";
import { RecipeFormAction, RecipeFormState } from "./utils";

interface RecipeFormProps {
  state: RecipeFormState;
  dispatch: Dispatch<RecipeFormAction>;
  sx?: SxProps;
  disabled?: boolean;
  forStaff?: boolean;
  onAiGenerateClicked: null | (() => void);
  onAiTranscribeClicked?: () => void;
  onImportFromLinkClicked?: () => void;
  openScaleRecipe: () => void;
}

const RecipeForm = ({
  state,
  dispatch,
  sx,
  disabled,
  forStaff,
  onAiGenerateClicked,
  onAiTranscribeClicked,
  onImportFromLinkClicked,
  openScaleRecipe,
}: RecipeFormProps) => {
  const [nutrientsOpen, setNutrientsOpen] = useState(false);
  const { isRecipeAndMenuItemRedesignEnabled } = useNutritionFlags();
  const uiVariant: FoodFormUiVariant = isRecipeAndMenuItemRedesignEnabled ? "redesign" : "legacy";

  const { data: scoringSystemData, loading: loadingScoringSystem } = useMyScoringSystemQuery();
  const scoringSystem = scoringSystemData?.myScoringSystem;

  const nutrientsModal = nutrientsOpen && (
    <NutrientAmountModal
      open={nutrientsOpen}
      onClose={() => setNutrientsOpen(false)}
      servingAmounts={state.ingredients.map(sa => ({
        ...sa,
        amount: sa.amount / state.serving.perRecipeYield,
      }))}
    />
  );

  let content: ReactNode;

  if (uiVariant === "redesign") {
    const cardSx = {
      bgcolor: "common.white",
      border: 1,
      borderColor: "greyscale.200",
      borderRadius: 1,
      p: 3,
    };

    content = (
      <Box sx={{ bgcolor: "greyscale.50", p: 3, ...sx }}>
        <Box sx={{ containerType: "inline-size", width: "100%", minWidth: 644 }}>
          <Box
            sx={{
              display: "grid",
              // One-col floor 644 (border-box, padding included). Split only when both floors + gap fit.
              gridTemplateColumns: "minmax(644px, 1fr)",
              gap: 3,
              alignItems: "start",
              "@container (min-width: 1148px)": {
                gridTemplateColumns: "minmax(644px, 2fr) minmax(480px, 1fr)",
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
              <Box sx={cardSx}>
                {loadingScoringSystem ? (
                  <Loading />
                ) : (
                  <Details forStaff={forStaff} disabled={disabled} state={state} dispatch={dispatch} scoringSystem={scoringSystem} />
                )}
              </Box>
              <Box sx={cardSx}>
                <Ingredients
                  forStaff={forStaff}
                  disabled={disabled}
                  state={state}
                  dispatch={dispatch}
                  setNutrientsOpen={setNutrientsOpen}
                  onAiGenerateClicked={onAiGenerateClicked}
                  onAiTranscribeClicked={onAiTranscribeClicked}
                  onImportFromLinkClicked={onImportFromLinkClicked}
                  openScaleRecipe={openScaleRecipe}
                />
              </Box>
              <Box sx={cardSx}>
                <Instructions disabled={disabled} steps={state.steps} dispatch={dispatch} />
              </Box>
              <Box sx={cardSx}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  Notes
                </Typography>
                <TextField
                  disabled={disabled}
                  fullWidth
                  value={state.note}
                  onChange={event => dispatch({ type: "CHANGE_NOTE", value: event.target.value })}
                  placeholder="Add a note to the recipe"
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
              <Box sx={cardSx}>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  Image
                </Typography>
                <Images disabled={disabled} dispatch={dispatch} state={state} />
              </Box>
              <Box sx={cardSx}>
                <RecipeFormServing
                  disabled={disabled}
                  state={state}
                  dispatch={dispatch}
                  openScaleRecipe={openScaleRecipe}
                  forStaff={forStaff}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        {nutrientsModal}
      </Box>
    );
  } else {
    content = (
      <Box sx={{ gap: 3, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "space-between",
            alignItems: "stretch",
            textOverflow: "auto",
            ...sx,
          }}
        >
          {/** Left panel */}
          <Box sx={{ flex: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {loadingScoringSystem ? (
                <Loading />
              ) : (
                <Details forStaff={forStaff} disabled={disabled} state={state} dispatch={dispatch} scoringSystem={scoringSystem} />
              )}
              <RecipeFormServing
                disabled={disabled}
                state={state}
                dispatch={dispatch}
                openScaleRecipe={openScaleRecipe}
                forStaff={forStaff}
              />
            </Box>
          </Box>

          {/** Right panel */}
          <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}>
            <Images disabled={disabled} dispatch={dispatch} state={state} />
          </Box>
        </Box>

        <Divider />

        {/** Ingredients and Directions/Notes */}
        <Box sx={{ display: "flex", flexDirection: "row", gap: 6 }}>
          <Box sx={{ flex: 1 }}>
            <Ingredients
              disabled={disabled}
              state={state}
              dispatch={dispatch}
              setNutrientsOpen={setNutrientsOpen}
              onAiGenerateClicked={onAiGenerateClicked}
              onAiTranscribeClicked={onAiTranscribeClicked}
              onImportFromLinkClicked={onImportFromLinkClicked}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Instructions disabled={disabled} steps={state.steps} dispatch={dispatch} />
            <Typography variant="h4" sx={{ pt: 4 }}>
              Notes
            </Typography>
            <TextField
              disabled={disabled}
              fullWidth
              value={state.note}
              onChange={e =>
                dispatch({
                  type: "CHANGE_NOTE",
                  value: e.target.value,
                })
              }
              placeholder="Add a note to the recipe"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" disablePointerEvents>
                    <Typography fontWeight="bold" color={disabled ? "text.disabled" : "inherit"}>
                      *
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        {nutrientsModal}
      </Box>
    );
  }

  return <FoodFormUiProvider variant={uiVariant}>{content}</FoodFormUiProvider>;
};

export default RecipeForm;
